import { useAppStore } from '@core/store'
import { Crosshair, X } from 'lucide-react'
import {
  coordFrameFor,
  coordFrameLabel,
  coordReadout,
  type CoordFrame,
} from './coordReadout'

// HUD with the live cursor coordinates. Two readouts:
//   • Absoluta: the picked point in the IFC axis convention (Z up, Y
//     north), see coordReadout.ts. Labelled X/Y/Z (file coordinates)
//     or E/N/Cota when a georeference placement is applied.
//   • Relativa: only when an origin is set, shows dX/dY/dZ from
//     that origin. Useful for distance-from-a-corner style queries
//     without having to start a measurement tool.
//
// Two buttons at the bottom: "definir origem" snapshots the current
// cursor position as the relative origin, "limpar" removes it.
// The marker rendering for the cursor and the origin is handled by
// useCoordsMarkers; here we only deal with the readout panel.

const FACTOR: Record<'mm' | 'cm' | 'm' | 'km', number> = {
  mm: 1000,
  cm: 100,
  m: 1,
  km: 0.001,
}

function CoordinatesHUD() {
  const enabled = useAppStore((s) => s.showCoordinates)
  const cursor = useAppStore((s) => s.cursorWorld)
  const reference = useAppStore((s) => s.coordsReference)
  const setReference = useAppStore((s) => s.setCoordsReference)
  const setShowCoordinates = useAppStore((s) => s.setShowCoordinates)
  const setAnnotationPlaceMode = useAppStore(
    (s) => s.setAnnotationPlaceMode,
  )
  const originPickMode = useAppStore((s) => s.coordsOriginPickMode)
  const setOriginPickMode = useAppStore(
    (s) => s.setCoordsOriginPickMode,
  )
  const units = useAppStore((s) => s.measureUnits)
  const snapClass = useAppStore((s) => s.snapClass)
  const measureMode = useAppStore((s) => s.measureMode)
  const placeMode = useAppStore((s) => s.annotationPlaceMode)
  const cycleSnap = useAppStore((s) => s.cycleSnapClass)
  const frame: CoordFrame = useAppStore((s) =>
    coordFrameFor(
      s.models.map((m) => m.id),
      s.geoBaselines,
      s.geoOverrides,
    ),
  )
  if (!enabled) return null
  const showSnap =
    measureMode !== null || placeMode === 'coord'

  const dismiss = () => {
    setShowCoordinates(false)
    setOriginPickMode(false)
    if (placeMode === 'coord') setAnnotationPlaceMode(null)
  }

  const fmt = (v: number) => {
    const scaled = v * FACTOR[units]
    const digits = units === 'mm' ? 0 : units === 'cm' ? 1 : units === 'm' ? 3 : 5
    return scaled.toFixed(digits)
  }
  const sign = (v: number) => (v >= 0 ? '+' : '')

  const onPickOriginByClick = () => {
    setOriginPickMode(!originPickMode)
  }

  return (
    <div
      role="status"
      aria-label="Coordenadas do cursor"
      className="pointer-events-auto absolute bottom-3 left-3 z-50 rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] px-2 py-1.5 font-mono text-[11px] text-[var(--color-text)] shadow-md"
    >
      <div className="mb-1 flex items-center justify-between gap-3">
        <span className="text-[9px] uppercase tracking-wider text-[var(--color-text-muted)]">
          Coordenadas ({units})
        </span>
        {showSnap && (
          <button
            type="button"
            onClick={cycleSnap}
            title="Snap activo (Tab cicla: tudo → ponto → aresta → face)"
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[9px] uppercase tracking-wider text-[var(--color-accent)] hover:text-[var(--color-text)]"
          >
            Snap: {snapClass === 'all' ? 'tudo' : snapClass === 'point' ? 'ponto' : snapClass === 'line' ? 'aresta' : 'face'} ⇥
          </button>
        )}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setReference([0, 0, 0])}
            title={
              frame === 'georref'
                ? 'Usar a origem do sistema de coordenadas (E 0, N 0, cota 0) como referência das coordenadas relativas'
                : 'Usar a origem do ficheiro IFC (0, 0, 0) como referência das coordenadas relativas'
            }
            className="rounded border border-[var(--color-border)] px-1 py-0.5 text-[9px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-accent)]"
          >
            <span className="font-mono">0,0,0</span>
          </button>
          <button
            type="button"
            onClick={onPickOriginByClick}
            aria-pressed={originPickMode}
            title="Activa modo de picar origem: o próximo click no modelo (com snap) define a origem das coordenadas relativas"
            className={`rounded border px-1 py-0.5 text-[9px] ${
              originPickMode
                ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-accent)]'
            }`}
          >
            <Crosshair size={10} className="inline-block" />
            <span className="ml-1">
              {originPickMode ? 'a picar...' : 'Picar'}
            </span>
          </button>
          {reference && (
            <button
              type="button"
              onClick={() => setReference(null)}
              title="Limpar origem das coordenadas relativas"
              className="rounded border border-[var(--color-border)] px-1 py-0.5 text-[9px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
            >
              <X size={10} className="inline-block" />
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            title="Fechar HUD de coordenadas (e sair do modo Coordenadas se estiver activo)"
            className="rounded border border-[var(--color-border)] px-1 py-0.5 text-[10px] font-bold text-[var(--color-text-muted)] hover:bg-red-500/20 hover:text-red-400"
          >
            ×
          </button>
        </div>
      </div>

      <div
        className="text-[9px] uppercase tracking-wider text-[var(--color-text-muted)]"
        title={`Coordenadas ${coordFrameLabel(frame)}, com o Z (cota) para cima e o Y (Norte) em planta, como no IFC.`}
      >
        Absoluta · {coordFrameLabel(frame)}
      </div>
      {cursor ? (
        <div className="grid grid-cols-[28px_auto] gap-x-2 gap-y-0">
          {coordReadout(cursor, frame).map((a, i) => (
            <Axis key={a.label} label={a.label} index={i} value={fmt(a.value)} />
          ))}
        </div>
      ) : (
        <div className="text-[var(--color-text-dim)]">sem ponto sob o cursor</div>
      )}

      {reference && (
        <>
          <div className="mt-1.5 text-[9px] uppercase tracking-wider text-cyan-300">
            Relativa à origem
          </div>
          {cursor ? (
            <div className="grid grid-cols-[28px_auto] gap-x-2 gap-y-0">
              {coordReadout(
                [
                  cursor[0] - reference[0],
                  cursor[1] - reference[1],
                  cursor[2] - reference[2],
                ],
                frame,
              ).map((a, i) => (
                <Axis
                  key={a.label}
                  label={`Δ${a.label}`}
                  index={i}
                  value={`${sign(a.value)}${fmt(a.value)}`}
                />
              ))}
            </div>
          ) : (
            <div className="text-[var(--color-text-dim)]">-</div>
          )}
          <div className="mt-1 text-[9px] text-[var(--color-text-dim)]">
            Origem [
            {coordReadout(reference, frame)
              .map((a) => fmt(a.value))
              .join(', ')}
            ]
          </div>
        </>
      )}
    </div>
  )
}

const AXIS_COLOURS = ['text-red-400', 'text-green-400', 'text-sky-400']

function Axis({
  label,
  index,
  value,
}: {
  label: string
  index: number
  value: string
}) {
  return (
    <>
      <span className={AXIS_COLOURS[index]}>{label}</span>
      <span>{value}</span>
    </>
  )
}

export default CoordinatesHUD
